import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import '@splidejs/react-splide/css'
import React, {
  Children,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useMediaQuery } from 'src/utils/useMediaQuery'
import * as Styles from './draggableSlider.module.scss'

/**
 * Imperative handle exposed by `DraggableSlider` via `ref`.
 */
export type DraggableSliderHandle = {
  /**
   * Attempt to move the slider to the specified index.
   *
   * @param index The index to which the slider should move. This should be a non-negative integer.
   */
  goTo: (index: number) => void
}

/**
 * Props for the `DraggableSlider` component.
 */
export type DraggableSliderProps = PropsWithChildren<{
  /**
   * ARIA label for the slider, used for accessibility purposes.
   */
  ariaLabel?: string
  /**
   * The index of the slide to display when the slider first mounts.
   * Only used during initialisation; changing this value after mount has no effect.
   */
  startIndex?: number
  /**
   * Callback function that is called whenever the slider's index changes, either due to a drag or a move event.
   *
   * @param newIndex The new index of the slider after the change.
   * @param prevIndex The previous index of the slider before the change.
   */
  onSlideChange?: (newIndex: number, prevIndex: number) => void
}>

/**
 * A draggable slider component that creates a slide for each child element passed to it.
 */
export const DraggableSlider = forwardRef<
  DraggableSliderHandle,
  DraggableSliderProps
>(({ children, ariaLabel, startIndex, onSlideChange }, ref) => {
  const splideRef = useRef<InstanceType<typeof Splide>>(null)
  const lastReportedIndexRef = useRef<number>(-1)
  const startIndexRef = useRef(startIndex ?? 0)
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const childArray = useMemo(() => Children.toArray(children), [children])

  useImperativeHandle(ref, () => ({
    goTo: (index: number) => {
      splideRef.current?.go(index)
    },
  }))

  /**
   * Reports the new index of the slider if it has changed since the last report.
   *
   * This function is used to notify the parent component of index changes, ensuring that the `onSlideChange` callback
   * is only called when the index actually changes, regardless of which event (move or drag) triggered the change.
   */
  const notifyIndexChange = useCallback(
    (newIndex: number, prevIndex: number) => {
      if (newIndex === lastReportedIndexRef.current) {
        return
      }

      lastReportedIndexRef.current = newIndex
      onSlideChange?.(newIndex, prevIndex)
    },
    [onSlideChange]
  )

  /**
   * Handles the slider's move event, notifying of index changes after the move has settled.
   */
  const handleSliderMove = useCallback(
    (splideInstance: unknown, newIndex: number, prevIndex: number) => {
      notifyIndexChange(newIndex, prevIndex)
    },
    [notifyIndexChange]
  )

  /**
   * Handles the slider's dragging event, notifying of index changes based on the nearest slide to the current
   * position.
   *
   * This is necessary because the `onMove` event only fires after the drag has completed, and we want to report index
   * changes during the drag as well to update any UI elements that depend on the current slide index while the user
   * is dragging.
   */
  const handleSliderDragging = useCallback(() => {
    const splideInstance = splideRef.current
    const moveComponent = splideInstance?.splide?.Components?.Move

    if (!moveComponent) {
      return
    }

    const currentPosition = moveComponent.getPosition()
    const nearestIndex = moveComponent.toIndex(currentPosition)
    notifyIndexChange(nearestIndex, lastReportedIndexRef.current)
  }, [notifyIndexChange])

  const splideOptions = {
    start: startIndexRef.current,
    fixedWidth: '100%',
    autoHeight: true,
    arrows: false,
    pagination: false,
    noDrag: 'a',
    reducedMotion: prefersReducedMotion,
  }

  return (
    <Splide
      ref={splideRef}
      hasTrack={false}
      options={splideOptions}
      label={ariaLabel}
      onMove={handleSliderMove}
      onDragging={handleSliderDragging}
    >
      <SplideTrack>
        {childArray.map(child => (
          <SplideSlide
            key={React.isValidElement(child) ? child.key : undefined}
            className={Styles.draggableSliderItem}
          >
            {child}
          </SplideSlide>
        ))}
      </SplideTrack>
    </Splide>
  )
})
